export const VAULT_REGION_DB = { US: 'US', EU: 'EU', UK: 'UK', CA: 'CA' } as const;
export type VaultRegionType = (typeof VAULT_REGION_DB)[keyof typeof VAULT_REGION_DB];

export const VAULT_ACCOUNT_STATUS_DB = {
  PENDING: 'pending',
  PENDING_CONFIRMATION: 'pending_confirmation',
  CONFIRMED: 'confirmed',
  INCOMPLETE: 'incomplete',
  FAILED: 'failed',
  CLOSED: 'closed',
} as const;
export type VaultAccountStatusType = (typeof VAULT_ACCOUNT_STATUS_DB)[keyof typeof VAULT_ACCOUNT_STATUS_DB];

export const SECURE_LINK_STATUS_DB = {
  ACTIVE: 'active',
  VIEWED: 'viewed',
  PENDING_VERIFICATION: 'pending_verification',
  COMPLETED: 'completed',
  EXPIRED: 'expired',
  REVOKED: 'revoked',
} as const;
export type SecureLinkStatusType = (typeof SECURE_LINK_STATUS_DB)[keyof typeof SECURE_LINK_STATUS_DB];

export const SECURE_LINK_MODE_DB = { SHARE: 'share', VERIFY: 'verify' } as const;
export type SecureLinkModeType = (typeof SECURE_LINK_MODE_DB)[keyof typeof SECURE_LINK_MODE_DB];

export const SECURE_LINK_VIA_DB = {
  EMAIL: 'email',
  SMS: 'sms',
  WHATSAPP: 'whatsapp',
  TELEGRAM: 'telegram',
  COPY: 'copy',
} as const;
export type SecureLinkViaType = (typeof SECURE_LINK_VIA_DB)[keyof typeof SECURE_LINK_VIA_DB];

export const SECURE_LINK_AUDIENCE_DB = { RECIPIENT: 'recipient', PROXY: 'proxy' } as const;
export type SecureLinkAudienceType = (typeof SECURE_LINK_AUDIENCE_DB)[keyof typeof SECURE_LINK_AUDIENCE_DB];

export const SECURE_LINK_EXPIRY_DB = { H24: '24h', D7: '7d', D30: '30d' } as const;
export type SecureLinkExpiryType = (typeof SECURE_LINK_EXPIRY_DB)[keyof typeof SECURE_LINK_EXPIRY_DB];

export type VaultHolderType = 'business' | 'individual';

export type RailMetaType = { code: string; label: string; hint: string };

export type FieldMetaType = {
  key: string;
  label: string;
  placeholder: string;
  pattern: string | null;
  sensitive: boolean;
  optional: boolean;
  rails: string[];
};

export type RegionMetaType = {
  label: string;
  vault: string;
  currency: string;
  country: string;
  banks: string[];
  rails: RailMetaType[];
  fields: FieldMetaType[];
};

export type MyVaultCatalogType = { regions: Record<VaultRegionType, RegionMetaType> };

export type RailLabelType = { code: string; label: string };

export type VaultAccountType = {
  id: string;
  ref_id: string;
  region: VaultRegionType;
  region_label: string;
  vault: string;
  country: string;
  bank_name: string;
  bank_is_other: boolean;
  nickname: string;
  holder_type: VaultHolderType;
  rails: string[];
  rail_labels: RailLabelType[];
  missing_rails: RailLabelType[];
  status: VaultAccountStatusType;
  status_label: string;
  masked_number: string;
  record_version: number;
  record_hash: string;
  record_hash_short: string;
  blockchain_id: string;
  initials: string;
  created_by: { id: string; email: string };
  date_created: string;
  date_modified: string;
  closed_at: string | null;
  is_data_from_blockchain: boolean;
};

export type RailViewFieldType = { key: string; label: string; value: string; sensitive: boolean };
export type RailViewType = { rail: string; label: string; blockchain_id: string; fields: RailViewFieldType[] };

export type VaultAccountDetailsType = VaultAccountType & {
  rail_views: RailViewType[];
  versions: { version: number; hash: string; status: string; blockchain_id: string; date_created: string }[];
};

export type SecureLinkStampType = { kind: 'verified' | 'opened' | 'revoked'; at: string } | null;

export type SecureLinkType = {
  id: string;
  vault_account: string;
  account_label: string;
  rail: string;
  rail_label: string;
  mode: SecureLinkModeType;
  via: SecureLinkViaType;
  audience: SecureLinkAudienceType;
  sent_to: string;
  named_only: boolean;
  one_time: boolean;
  expiry_code: SecureLinkExpiryType;
  expires_at: string;
  status: SecureLinkStatusType;
  status_label: string;
  controls: string;
  archived: boolean;
  url: string;
  date_created: string;
  first_opened_at: string | null;
  completed_at: string | null;
  revoked_at: string | null;
  can_revoke: boolean;
  can_resend: boolean;
  has_verification: boolean;
  stamp: SecureLinkStampType;
};

export type SecureLinkVerificationType = {
  session_id: string;
  status: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  email: string;
  phone_masked: string;
  name_from_document: string;
  role: string;
  date_modified: string;
  raw_result_hash: string;
};

export type VaultEventType = {
  id: string;
  event: string;
  category: 'vault' | 'links' | 'verify';
  actor_label: string;
  object_type: string;
  object_id: string | null;
  object_label: string;
  detail: string;
  date_created: string;
};

export type PublicSecureLinkStateType = {
  state: SecureLinkStatusType;
  organization?: string;
  rail?: string;
  rail_label?: string;
  requires_verification?: boolean;
  named_only?: boolean;
  named_channel?: '' | 'email' | 'sms';
  expires_at?: string;
};

export type RevealedInstructionsType = {
  bank_name: string;
  rail: string;
  rail_label: string;
  fields: { key: string; label: string; value: string }[];
  record: { version: number; hash: string; ref_id: string; blockchain_id: string; saved_at: string };
  shared_by: string;
  access: RevealAccessType;
};

export type SecureLinkVerifyStatusType = {
  status: 'not_initialized' | 'initialized' | 'in_progress' | 'failed' | 'succeed';
  phone_masked?: string;
  start_new_session_enable_after_sec: number;
  session_id: string | null;
  can_resend: boolean;
};

export type RevealAccessType = {
  verified: boolean;
  one_time: boolean;
  single_view: boolean;
  reason: 'verified' | 'one_time' | '';
};
